import { StepType } from "@redotech/redo-model/advanced-flow/common";
import { PartialFlowStep } from "../steps";
import { replaceNextElement } from "./replace-next-element";

export function deleteElement(
  stepIdToDelete: string,
  steps: PartialFlowStep[],
) {
  const step = steps.find((step) => step.id === stepIdToDelete);
  if (!step) {
    console.error(`Element to delete not found: ${stepIdToDelete}`);
    return steps;
  }

  let newNext = "";

  // For RULE_CONDITIONS and RULE_ACTIONS, delete all children and unassign parents
  // Don't try to reconnect because these nodes have special two-branch logic
  if (step.type === StepType.RULE_CONDITIONS) {
    if ("nextFalseId" in step && step.nextFalseId) {
      steps = deleteElementAndAllChildren(step.nextFalseId, steps);
    }
    if ("nextTrueId" in step && step.nextTrueId) {
      steps = deleteElementAndAllChildren(step.nextTrueId, steps);
    }
    // Set newNext to undefined so parents will be unassigned
    newNext = "";
  } else if (step.type === StepType.RULE_ACTIONS) {
    if ("nextId" in step && step.nextId) {
      steps = deleteElementAndAllChildren(step.nextId, steps);
    }
    // Set newNext to undefined so parents will be unassigned
    newNext = "";
  } else {
    // For other step types, use existing logic
    if ("nextId" in step && step.nextId) {
      newNext = step.nextId;
    }
    if ("nextFalseId" in step && step.nextFalseId) {
      steps = deleteElementAndAllChildren(step.nextFalseId, steps);
    }
    if ("nextTrueId" in step && step.nextTrueId) {
      newNext = step.nextTrueId;
    }
  }
  if ("matchingNextId" in step && step.matchingNextId) {
    newNext = step.matchingNextId;
  }
  if ("nonMatchingNextId" in step && step.nonMatchingNextId) {
    steps = deleteElementAndAllChildren(step.nonMatchingNextId, steps);
  }
  if ("acceptNextId" in step && step.acceptNextId) {
    newNext = step.acceptNextId;
  }
  if ("declineNextId" in step && step.declineNextId) {
    steps = deleteElementAndAllChildren(step.declineNextId, steps);
  }
  // Handle AB test variants - use first variant's nextId as new next if available
  if ("variants" in step && step.variants && step.variants.length > 0) {
    const firstVariant = step.variants[0];
    if (firstVariant && firstVariant.nextId) {
      newNext = firstVariant.nextId;
    }
    for (let i = 1; i < step.variants.length; i++) {
      const variant = step.variants[i];
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (variant.nextId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        steps = deleteElementAndAllChildren(variant.nextId, steps);
      }
    }
  }
  // Handle N-ary segment edges — keep default edge, delete the rest
  if ("edges" in step && step.edges && step.edges.length > 0) {
    const defaultEdgeId =
      "defaultEdgeId" in step ? step.defaultEdgeId : undefined;
    for (const edge of step.edges) {
      if (edge.edgeId === defaultEdgeId) {
        if (edge.nextId) {
          newNext = edge.nextId;
        }
      } else if (edge.nextId) {
        steps = deleteElementAndAllChildren(edge.nextId, steps);
      }
    }
  }
  replaceNextElement(stepIdToDelete, newNext || undefined, steps);
  return steps.filter((step) => step.id !== stepIdToDelete);
}

export function deleteElementAndAllChildren(
  stepIdToDelete: string,
  steps: PartialFlowStep[],
): PartialFlowStep[] {
  const step = steps.find((step) => step.id === stepIdToDelete);
  if (!step) {
    console.error(`Element to delete not found: ${stepIdToDelete}`);
    return steps;
  }

  if ("nextId" in step && step.nextId) {
    steps = deleteElementAndAllChildren(step.nextId, steps);
  }
  if ("nextFalseId" in step && step.nextFalseId) {
    steps = deleteElementAndAllChildren(step.nextFalseId, steps);
  }
  if ("nextTrueId" in step && step.nextTrueId) {
    steps = deleteElementAndAllChildren(step.nextTrueId, steps);
  }
  if ("matchingNextId" in step && step.matchingNextId) {
    steps = deleteElementAndAllChildren(step.matchingNextId, steps);
  }
  if ("nonMatchingNextId" in step && step.nonMatchingNextId) {
    steps = deleteElementAndAllChildren(step.nonMatchingNextId, steps);
  }
  if ("acceptNextId" in step && step.acceptNextId) {
    steps = deleteElementAndAllChildren(step.acceptNextId, steps);
  }
  if ("declineNextId" in step && step.declineNextId) {
    steps = deleteElementAndAllChildren(step.declineNextId, steps);
  }
  // Add handling for AB test variants
  if ("variants" in step && step.variants) {
    for (const variant of step.variants) {
      if (variant.nextId) {
        steps = deleteElementAndAllChildren(variant.nextId, steps);
      }
    }
  }
  // Handle split treatments
  if ("treatments" in step && step.treatments) {
    for (const treatment of step.treatments) {
      if (treatment.nextId) {
        steps = deleteElementAndAllChildren(treatment.nextId, steps);
      }
    }
  }
  // Handle N-ary segment edges
  if ("edges" in step && step.edges) {
    for (const edge of step.edges) {
      if (edge.nextId) {
        steps = deleteElementAndAllChildren(edge.nextId, steps);
      }
    }
  }
  return steps.filter((step) => step.id !== stepIdToDelete);
}
